import React, { useEffect, useRef, useState } from 'react';
import * as api from '../api';
import Transcript from '../components/Transcript';
import RatingsPage from './RatingsPage';
import CallTypeDropdown from "../components/CallTypeDropdown";
import AgentDropdown from "../components/AgentDropdown";


export default function History() {

  const [loadingId, setLoadingId] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [filterTypeName, setFilterTypeName] = useState("all");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [filterAgent, setFilterAgent] = useState({ id: "", name: "" });
  const todayISO = new Date().toISOString().slice(0, 10);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState(todayISO);

  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 700);
    return () => clearTimeout(t);
  }, [q]);

  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState('unrated');

  const [page, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 5 : 8
  );

  const monthBgShort = ["Ян", "Фев", "Мар", "Апр", "Май", "Юни", "Юли", "Авг", "Сеп", "Окт", "Ное", "Дек"];

  function normalizeIsoUtc(s) {
    if (!s) return s;
    if (!/[zZ]$/.test(s) && !/[+-]\d{2}:\d{2}$/.test(s)) return s + "Z";
    return s;
  }

  function formatBgDate(isoStr) {
    if (!isoStr) return "";
    const d = new Date(normalizeIsoUtc(isoStr));
    if (isNaN(d)) return "";
    const day = d.getDate();
    const mon = monthBgShort[d.getMonth()];
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${day} ${mon} ${hh}:${mm}`;
  }

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setPageSize(e.matches ? 5 : 8);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const requestIdRef = useRef(0);
  useEffect(() => {
    const myReq = ++requestIdRef.current;
    (async () => {
      try {
        const resp = await api.historyPaged({
          page,
          perPage: pageSize,
          q: qDebounced,
          tab,
          typeName: filterTypeName,
          operator_name: filterAgent?.name?.trim() || "",
          from: filterFrom || "",
          to: filterTo || ""
        });
        if (myReq !== requestIdRef.current) return;
        setItems(resp.items || []);
        setTotalPages(resp.total_pages || 1);
        setRatedCount(resp?.counts?.rated ?? 0);
        setUnratedCount(resp?.counts?.unrated ?? 0);
      } catch (e) {
        if (myReq !== requestIdRef.current) return;
        console.error(e);
        setItems([]);
        setTotalPages(1);
        setRatedCount(0);
        setUnratedCount(0);
      }
    })();
  }, [
    page,
    pageSize,
    qDebounced,
    tab,
    filterTypeName,
    filterAgent,
    filterFrom,
    filterTo
  ]);


  const [ratedCount, setRatedCount] = useState(0);
  const [unratedCount, setUnratedCount] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    qDebounced,
    tab,
    filterTypeName,
    filterAgent,
    filterFrom,
    filterTo
  ]);


  const [totalPages, setTotalPages] = useState(1);
  useEffect(() => {
    if (page > totalPages) setCurrentPage(totalPages);
  }, [totalPages, page]);

  const pageItems = items;

  const viewItem = async (it) => {
    try {
      setLoadingId(it.id);
      const t = await api.transcript(it.id);
      setSel({
        ...it,
        transcript: t.transcript,
        segments: Array.isArray(t.segments) ? t.segments : [],
        hasRating: !!it.has_rating
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingId(null);
    }
  };

  const handleExportExcel = async (id) => {
    try {
      setExcelBusy(true);
      await api.exportRatingExcel(id);
    } catch (e) {
      console.error(e);
      alert("Грешка при експорт на Excel файла.");
    } finally {
      setExcelBusy(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      setDownloadBusy(true);
      await api.downloadFile(id);
    } catch (e) {
      console.error(e);
      alert("Грешка при сваляне на файла.");
    } finally {
      setDownloadBusy(false);
    }
  };

  const [expTypeCode, setExpTypeCode] = useState("__ALL__");
  const isAllTypes = expTypeCode === "__ALL__";
  const [expAgent, setExpAgent] = useState({ id: "", name: "" });
  const [exportOpen, setExportOpen] = useState(false);
  const [tplOpts, setTplOpts] = useState([]);
  const [expTplId, setExpTplId] = useState('');

  useEffect(() => {
    if (!exportOpen || isAllTypes) {
      setTplOpts([]);
      setExpTplId("");
      return;
    }

    if (!expTypeCode) {
      setTplOpts([]);
      setExpTplId("");
      return;
    }

    (async () => {
      try {
        const list = await api.listTemplates(expTypeCode);
        const opts = Array.isArray(list) ? list : [];
        setTplOpts(opts);
        const def = opts.find(t => t.is_default) || opts[0];
        setExpTplId(def?.id || "");
      } catch (e) {
        console.error(e);
        setTplOpts([]);
        setExpTplId("");
      }
    })();
  }, [exportOpen, expTypeCode, isAllTypes]);

  const today = new Date();
  const y = today.getFullYear(), m = String(today.getMonth() + 1).padStart(2, "0"), d = String(today.getDate()).padStart(2, "0");
  const firstOfMonth = `${y}-${m}-01`;
  const todayStr = `${y}-${m}-${d}`;

  const [expFrom, setExpFrom] = useState(firstOfMonth);
  const [expTo, setExpTo] = useState(todayStr);
  const [expBusy, setExpBusy] = useState(false);

  useEffect(() => {
    const handler = () => {
      setSel(null);
    };

    window.addEventListener("popstate", handler);

    return () => window.removeEventListener("popstate", handler);
  }, []);

  const isDateRangeValid = !!expFrom && !!expTo && new Date(expFrom) <= new Date(expTo);
  const canExport =
    !expBusy &&
    isDateRangeValid &&
    (
      isAllTypes || (!!expTypeCode && !!expTplId)
    );

  const exportBtnClass = canExport
    ? "btn btn-primary hover:scale-[1.02] transition-transform"
    : "btn btn-ghost opacity-60 cursor-not-allowed";

  let exportDisabledReason = "";
  if (!isAllTypes && !expTypeCode) exportDisabledReason = "Избери тип разговор";
  else if (!isAllTypes && !expTplId) exportDisabledReason = "Избери темплейт";
  else if (!expFrom || !expTo) exportDisabledReason = "Попълни дати";
  else if (!isDateRangeValid) exportDisabledReason = "Невалиден период";
  else if (expBusy) exportDisabledReason = "Експорт в процес…";

  return (
    <div className="card rounded-2xl p-6">
      {!sel ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-bold whitespace-nowrap">Разговори</h2>
            <div className="flex items-center gap-2">
              <button
                className={`btn ${tab === 'unrated' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTab('unrated')}
              >
                Неоценени ({unratedCount})
              </button>
              <button
                className={`btn ${tab === 'rated' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTab('rated')}
              >
                Оценени ({ratedCount})
              </button>
            </div>
            <div className="ml-auto">
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Търсене по име…"
                className="shrink-0 w-40 sm:w-72 md:w-80 bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
              />

              {tab === 'rated' && (
                <button className="btn btn-muted ml-2" onClick={() => setExportOpen(true)}>
                  Експорт
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 mt-3">
            <div className="w-44">
              <CallTypeDropdown
                value={filterTypeName === "all" ? "" : filterTypeName}
                onChange={(v) => setFilterTypeName(v || "all")}
                emit="name"
                includeAll
                allLabel="Всички видове"
              />
            </div>
            <div className="w-44 sm:w-48">
              <AgentDropdown value={filterAgent} onChange={setFilterAgent} />
            </div>

            <div className="min-w-[150px]">
              <input
                type="date"
                className="bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm text-white outline-none w-full"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div className="min-w-[150px]">
              <input
                type="date"
                className="bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm text-white outline-none w-full"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                min={filterFrom || undefined}
              />
            </div>
          </div>
          <div className="mt-4 overflow-x-auto max-w-full">
            <table className="w-full table">
              <thead>
                <tr className="text-left">
                  <th className="text-left">Файл</th>
                  <th className="text-right">Вид</th>
                  <th className="text-right">Дата</th>
                  <th className="text-right">Агент</th>
                  {tab === 'rated' && <th className="text-right">Оценил</th>}
                  <th className="text-right">Статус</th>
                  <th className="text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length ? pageItems.map(h => (
                  <tr key={h.id}>
                    <td className="font-medium text-left">{h.filename}</td>

                    <td className="text-right">
                      <span
                        className={
                          "px-2 py-0.5 rounded-full text-[11px] " +
                          (h.call_type === "Кампания"
                            ? "bg-violet-900/30 border border-violet-700/40 text-white font-semibold"
                            : h.call_type === "Нова цесия"
                              ? "bg-sky-700/40 border border-sky-500/40 text-white font-semibold"
                              : h.call_type === "Редовен платец"
                                ? "bg-yellow-400/40 border border-yellow-400/60 text-white font-semibold"
                                : h.call_type === "Неспазено обещание"
                                  ? "bg-orange-500/50 border border-orange-600/60 text-white font-semibold"
                                  : h.call_type === "Без обещание" ||
                                    h.call_type === "Обещание" ||
                                    h.call_type === "Трето лице"
                                    ? "bg-emerald-500/40 border border-emerald-600/60 text-white font-semibold"
                                    : "bg-slate-700/30 border border-slate-600/40 text-slate-200")
                        }
                        title={h.call_type || "Вид"}
                      >
                        {h.call_type || "Вид"}
                      </span>
                    </td>


                    <td className="text-right text-slate-300">
                      {formatBgDate(h.created_at)}
                    </td>

                    <td className="text-right text-slate-300">
                      {h.operator_name}
                    </td>

                    {tab === 'rated' && (
                      <td className="text-right text-slate-300">
                        {h.evaluator_display && h.evaluator_display.trim()
                          ? h.evaluator_display
                          : "Не е посочен"}
                      </td>
                    )}

                    <td className="text-right">
                      {h.has_rating ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-900/30 border border-emerald-700/40 text-emerald-200 text-[11px]">
                          Оценен
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-700/30 border border-slate-600/40 text-slate-200 text-[11px]">
                          Неоценен
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-ghost flex items-center justify-center gap-2"
                        onClick={() => {
                          if (typeof window !== "undefined") {
                            window.history.pushState({ fromHistory: true }, "");
                          }
                          viewItem(h);
                        }}
                        disabled={loadingId === h.id}
                      >
                        {loadingId === h.id ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4 text-violet-400"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              ></path>
                            </svg>
                            Зарежда се...
                          </>
                        ) : (
                          "Преглед"
                        )}
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={tab === 'rated' ? 7 : 6} className="text-center py-10 text-slate-400">
                      Няма записи.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-start gap-2">
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setCurrentPage(p => p - 1)}>Назад</button>
              <div className="text-sm text-slate-400">Стр. {page} / {totalPages}</div>
              <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Напред</button>
            </div>
          )}

          {exportOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => setExportOpen(false)} />
              <div className="relative z-10 w-[min(92vw,620px)] rounded-2xl border border-[#1f2638] bg-[#0e1320] p-5">
                <div className="text-lg font-semibold mb-4">Експорт на оценени разговори</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300">От дата</label>
                    <input
                      type="date"
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expFrom}
                      onChange={e => setExpFrom(e.target.value)}
                      max={expTo}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300">До дата</label>
                    <input
                      type="date"
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expTo}
                      onChange={e => setExpTo(e.target.value)}
                      min={expFrom}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300">Тип разговор <span className="text-rose-400">*</span>
                    </label>
                    <CallTypeDropdown
                      value={expTypeCode === "__ALL__" ? "" : expTypeCode}
                      onChange={(v) => setExpTypeCode(v || "__ALL__")}
                      emit="code"
                      includeAll
                      allLabel="Всички"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300">Агент</label>
                    <AgentDropdown value={expAgent} onChange={setExpAgent} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300">Темплейт <span className="text-rose-400">*</span></label>
                    <select
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expTplId}
                      onChange={e => setExpTplId(e.target.value)}
                      disabled={isAllTypes || !expTypeCode || !tplOpts.length}
                    >
                      {isAllTypes ? (
                        <option value="">Всички</option>
                      ) : !expTypeCode ? (
                        <option value="">Изберете тип разговор</option>
                      ) : (
                        <>
                          {!tplOpts.length && <option value="">Няма темплейти за този тип</option>}
                          {tplOpts.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.is_default ? " (default)" : ""}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setExportOpen(false)}
                    title="Затвори без експорт"
                  >
                    Отказ
                  </button>
                  <button
                    type="button"
                    className={exportBtnClass}
                    disabled={!canExport}
                    aria-disabled={!canExport}
                    title={canExport ? "Експорт" : exportDisabledReason}
                    onClick={async () => {
                      if (!canExport) return;
                      try {
                        setExpBusy(true);
                        const queryType = isAllTypes ? undefined : expTypeCode;
                        const queryTplId = isAllTypes ? undefined : expTplId;

                        await api.exportRatingsRange(
                          expFrom,
                          expTo,
                          queryType,
                          queryTplId,
                          expAgent.id || ""
                        );

                        setExportOpen(false);
                      } catch (e) {
                        console.error(e);
                        alert(e?.message || "Грешка при експорт.");
                      } finally {
                        setExpBusy(false);
                      }
                    }}
                  >
                    {expBusy ? "Експорт…" : "Експорт"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-2.5">
            <button
              className="btn btn-ghost"
              onClick={() => {
                setSel(null);
              }}
            >
              Назад
            </button>
          </div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">
              Транскрипт — {sel.filename}
            </h2>
            <div className="flex items-center gap-2">
              {sel.hasRating && (
                <button
                  className="btn btn-muted flex items-center justify-center gap-2"
                  onClick={() => handleExportExcel(sel.id)}
                  disabled={excelBusy}
                >
                  {excelBusy ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-violet-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Експорт…
                    </>
                  ) : (
                    "Експорт Excel"
                  )}
                </button>
              )}
              <button
                className="btn btn-muted flex items-center justify-center gap-2"
                onClick={() => handleDownload(sel.id)}
                disabled={downloadBusy}
              >
                {downloadBusy ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-violet-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Сваляне…
                  </>
                ) : (
                  "Свали"
                )}
              </button>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-1/2 space-y-4">
              <div className="border border-[#1f2638] rounded-xl p-4 bg-[#0e1320]/40">
                <div className="max-h-[70vh] overflow-y-auto nice-scrollbar">
                  <Transcript text={sel.transcript} segments={sel.segments} />
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <RatingsPage
                fileId={sel.id}
                onSaved={() => setSel(prev => prev ? { ...prev, hasRating: true } : prev)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
